import type { MouseEvent, ReactNode } from "react";
import { Tabs, type BoxProps } from "@mantine/core";

/**
 * The bar: a row of slots, one of them the one you are in.
 *
 * A Mantine `Tabs` whose tabs are real `<a href>`s. `Tabs` supplies the roving tabindex and
 * arrow-key focus; `component="a"` keeps each slot an address you can bookmark, copy, or open
 * in a second window. Two callers -- the admin sections and the header's review switch -- and
 * the look is the same fact in both, so it is stated once here rather than twice in the two
 * files that draw a bar.
 *
 * A slot is the full height of the bar rather than a chip floating inside it: no vertical
 * padding on the bar, `radius={0}`, and the height coming off the slot's own padding. The
 * selected slot's underline then sits *on* the bar's bottom rule instead of a pixel above it
 * as a second line -- which is what `marginBottom: -1` buys, lapping each slot over whatever
 * rule the caller has put below.
 */

export function NavBar({
  label,
  value,
  children,
  ...box
}: {
  /**
   * Names the tablist. A bar of several slots is a set, and a set wants saying; a bar of one
   * does not -- the slot names itself, and a group name repeating it is noise.
   */
  label?: string;
  /**
   * The slot you are in, or `null` for none of them.
   *
   * `null` is not the same as a value no slot carries: Mantine reads it as "nothing selected
   * yet" and leaves every slot tabbable, where an unmatched value takes the roving tabindex
   * to -1 on all of them and the bar stops being reachable by keyboard at all.
   */
  value: string | null;
  children: ReactNode;
} & BoxProps) {
  return (
    <Tabs
      value={value}
      color="accent"
      radius={0}
      // Left on, Mantine calls `.click()` on whichever slot it focuses, so every arrow key
      // press would navigate. Off, focus moves and Enter follows the link.
      activateTabWithKeyboard={false}
      c="dimmed"
      styles={{
        root: { minWidth: 0, overflowX: "auto", overflowY: "hidden" },
        // `height` is inert when the bar sizes itself -- a percentage against an auto-height
        // parent is `auto`. It only bites for a caller that stretches the bar to a row it did
        // not set the height of, and there it is what carries that height down to the slots.
        //
        // Keep every slot reachable when even the compact icons exceed the available width.
        list: { height: "100%", flexWrap: "nowrap", width: "max-content", minWidth: "100%" },
        // `sm` rather than Mantine's `xs`: 12 + the 14px label + 12 + the 2px underline is a
        // 40px bar, where the stock tab is sized to sit above a panel it shares a page with
        // and reads as a strip of links rather than a row of chrome. A floor, not a height:
        // a stretched bar's slots grow past it.
        //
        // The `-1` is the lap: it puts the slot's bottom edge a pixel past the bar's, over
        // whatever rule the caller has drawn there, so the selected underline sits *on* that
        // rule instead of a pixel above it as a second line. Here rather than at the callers,
        // because a caller that also cancelled its own padding would lap twice.
        tab: { paddingBlock: "var(--mantine-spacing-sm)", marginBottom: -1, flexShrink: 0 },
      }}
      // Mantine's `default` variant also runs a rule under the whole list, in `dark-4` -- a
      // line the caller already draws, and not even the grey the rest of the chrome uses.
      // Transparent leaves the selected slot's underline, which is the part that says
      // anything. The hover wash stays; only the hover underline goes with it.
      __vars={{ "--tab-border-color": "transparent" }}
      {...box}
    >
      <Tabs.List aria-label={label}>{children}</Tabs.List>
    </Tabs>
  );
}

NavBar.Slot = function NavSlot({
  value,
  href,
  onClick,
  active,
  color,
  icon,
  label,
  compact = false,
  rightSection,
}: {
  /** What `NavBar`'s `value` is compared against. Not the href: a slot may point elsewhere
   * while it is the one you are in, and the two are different facts. */
  value: string;
  href: string;
  onClick: (event: MouseEvent) => void;
  active: boolean;
  /**
   * This slot's ink when it is the one you are in, overriding the bar's. For a section that
   * already has a colour everywhere else it appears -- admin is the whole of that set -- and
   * arriving in the bar is no reason for it to change.
   */
  color?: string;
  /** Drawn before the label, in the slot's own ink. */
  icon: ReactNode;
  label: string;
  /**
   * The mark alone, with the label as its accessible name and tooltip, to reduce scrolling
   * when a row cannot fit the words.
   */
  compact?: boolean;
  /**
   * Drawn after the label, inside the slot. For a slot that also carries a control of its
   * own -- the header's Review/Covers switch.
   */
  rightSection?: ReactNode;
}) {
  return (
    <Tabs.Tab
      value={value}
      // `Tabs.Tab` is a compound component, so `href` is not in its typed props at all;
      // `renderRoot` is the typed way to hand it an element of our own. `component="a"` still
      // earns its place beside it: it is what stops `UnstyledButton` handing the anchor the
      // `type="button"` it adds for its default element.
      component="a"
      // The label is passed through explicitly rather than riding along in the spread: an
      // anchor whose only content arrives as an anonymous prop is one nothing can check, and
      // a slot with no accessible name is a link a screen reader announces as its href.
      renderRoot={({ children, ...props }) => (
        <a {...props} href={href}>
          {children}
        </a>
      )}
      onClick={onClick}
      // Mantine puts this on `--tabs-color`, which is the underline's colour and, through the
      // `c` below, the label's -- so one prop lights the whole slot and the two cannot drift.
      color={color}
      // Compact hands the mark to `children` instead: as a `leftSection` beside nothing it
      // would still be paying the gap Mantine puts between a section and a label.
      leftSection={compact ? undefined : icon}
      rightSection={rightSection}
      aria-label={compact ? label : undefined}
      title={compact ? label : undefined}
      px={compact ? 10 : undefined}
      miw={compact ? 36 : undefined}
      // `default` sets no colour on the tab itself, so the bar's dimmed chrome ink is what an
      // unselected slot inherits. The selected one is lit to match its underline, and takes
      // the raised surface behind it -- without a fill the full-height slot is invisible and
      // its underline reads as a stray line at the bottom of the bar.
      bg={active ? "var(--mantine-color-default)" : undefined}
      c={active ? "var(--tabs-color)" : undefined}
    >
      {compact ? icon : label}
    </Tabs.Tab>
  );
};
